import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, BarChart2, CheckCircle, Info, Loader2, Trash2, XCircle } from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/Footer';
import { getCancellationStats, openBillingPortal, clearUserProfile } from '../utils/dashboardApi';
import useAuthModal from '../hooks/useAuthModal';

export default function CancelSubscriptionScreen() {
  const navigate = useNavigate();
  const authModal = useAuthModal();
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    async function loadStats() {
      try {
        const data = await getCancellationStats();
        setStats(data);
      } catch (err) {
        console.error('Failed to load stats:', err);
        setError('Failed to load your profile statistics.');
      } finally {
        setLoading(false);
      }
    }
    loadStats();
  }, []);

  const handleManageBilling = async () => {
    setCancelling(true);
    setError('');
    try {
      const url = await openBillingPortal();
      window.location.href = url;  // Stripe-hosted: cancel / update card / invoices
    } catch (err) {
      setError(err.message || 'Failed to open billing portal.');
      setCancelling(false);
    }
  };

  const handleClearProfile = async () => {
    const message = `DANGER: This will permanently DELETE all your ${stats?.interviews_started || 0} interviews and reset your skill profile. This action CANNOT be undone. Are you absolutely sure?`;
    if (!window.confirm(message)) {
      return;
    }

    setClearing(true);
    setError('');
    try {
      await clearUserProfile();
      setSuccess('Your profile data has been wiped clean.');
      setTimeout(() => navigate('/profile'), 2000);
    } catch (err) {
      setError(err.message || 'Failed to clear profile.');
      setClearing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-[#F6F7FB]">
        <SiteHeader authModal={authModal} />
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-10 w-10 animate-spin text-orange-500" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F7FB] text-[#1E293B]">
      <SiteHeader authModal={authModal} />

      <main className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6">
        <button
          onClick={() => navigate('/profile')}
          className="mb-6 flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Profile
        </button>

        <section className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
          <div className="bg-slate-900 p-8 text-center text-white">
            <h1 className="text-3xl font-bold">One last look...</h1>
            <p className="mt-2 text-slate-400">Here is what you have achieved so far</p>
          </div>

          <div className="p-6 sm:p-8">
            {error && (
              <div className="mb-6 flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                <XCircle className="h-5 w-5 flex-shrink-0" />
                {error}
              </div>
            )}
            {success && (
              <div className="mb-6 flex items-center gap-3 rounded-2xl border border-green-200 bg-green-50 p-4 text-sm text-green-700">
                <CheckCircle className="h-5 w-5 flex-shrink-0" />
                {success}
              </div>
            )}

            <div className="mb-10 grid grid-cols-2 gap-4">
              <div className="rounded-2xl border border-gray-100 bg-slate-50 p-6 text-center">
                <p className="text-4xl font-bold text-slate-900">{stats?.interviews_started || 0}</p>
                <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-gray-500">Interviews Started</p>
              </div>
              <div className="rounded-2xl border border-gray-100 bg-slate-50 p-6 text-center">
                <p className="text-4xl font-bold text-emerald-600">{stats?.interviews_completed || 0}</p>
                <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-gray-500">Interviews Completed</p>
              </div>
            </div>

            <div className="mb-8 space-y-4">
              <h3 className="text-lg font-bold text-slate-900">What happens next?</h3>
              <div className="flex gap-4 rounded-2xl border border-blue-100 bg-blue-50 p-4">
                <Info className="h-6 w-6 flex-shrink-0 text-blue-500" />
                <div className="text-sm text-blue-800">
                  <p className="font-bold">Subscription Access</p>
                  <p className="mt-1">You will keep all your Pro benefits until the end of your current billing period. After that, your account will revert to the Free plan.</p>
                </div>
              </div>
              <div className="flex gap-4 rounded-2xl border border-amber-100 bg-amber-50 p-4">
                <BarChart2 className="h-6 w-6 flex-shrink-0 text-amber-500" />
                <div className="text-sm text-amber-800">
                  <p className="font-bold">Data Preservation</p>
                  <p className="mt-1">We will keep your interview history safe unless you choose to delete it below. You can resume your progress anytime by resubscribing.</p>
                </div>
              </div>
            </div>

            <div className="mt-12 flex flex-col gap-4 border-t border-gray-100 pt-8">
              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  onClick={handleManageBilling}
                  disabled={cancelling || clearing}
                  className="flex-1 rounded-2xl bg-slate-900 py-4 text-sm font-bold text-white transition hover:bg-slate-800 disabled:opacity-50"
                >
                  {cancelling ? 'Opening…' : 'Manage billing'}
                </button>
                <button
                  onClick={() => navigate('/profile')}
                  className="flex-1 rounded-2xl border border-gray-200 py-4 text-sm font-bold text-gray-700 transition hover:bg-gray-50"
                >
                  Keep My Subscription
                </button>
              </div>

              <div className="mt-6 flex flex-col items-center gap-4 rounded-2xl border-2 border-dashed border-red-100 p-6 text-center">
                <div className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="h-5 w-5" />
                  <span className="text-sm font-bold">Danger Zone</span>
                </div>
                <p className="max-w-md text-xs text-gray-500">
                  If you want to completely wipe your data and start from scratch, use the button below. This will delete all interviews and skills progress.
                </p>
                <button
                  onClick={handleClearProfile}
                  disabled={clearing || cancelling}
                  className="inline-flex items-center gap-2 rounded-xl border border-red-200 px-6 py-2 text-xs font-bold text-red-600 transition hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                  {clearing ? 'Wiping Data...' : 'Delete My Interviews & Clear Profile'}
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
